/**
 * Account Number Service
 *
 * Auto-generates monotonic account numbers for customers in the format:
 *   TF-YYYY-NNNN  (e.g. "TF-2026-0042")
 *
 * A per-tenant counter document at tenants/{tenantId}/counters/customer_account_number
 * is incremented inside a Firestore transaction to guarantee uniqueness even under
 * concurrent creation.
 *
 * Each customer — company or child site — gets its own account number. Child sites
 * do NOT share their parent company's number.
 */

import {
  doc,
  getDoc,
  runTransaction,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  orderBy,
} from 'firebase/firestore';
import { db } from '../firebase';
import { tenantIdReadUnion } from '@/lib/tenant';
import type { Customer } from '@/types';

const PREFIX = 'TF';
const PAD_LENGTH = 4;

function counterRef(tenantId: string) {
  return doc(db, 'tenants', tenantId, 'counters', 'customer_account_number');
}

function formatAccountNumber(year: number, seq: number): string {
  return `${PREFIX}-${year}-${String(seq).padStart(PAD_LENGTH, '0')}`;
}

/**
 * Reserve the next account number for the given tenant + year.
 *
 * Transactional so two concurrent customer-create calls can't collide.
 * Counter layout: { [year]: nextSeq, lastYear, lastSeq }
 */
export async function reserveAccountNumber(tenantId: string): Promise<string> {
  const year = new Date().getFullYear();
  const ref = counterRef(tenantId);

  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? (snap.data() as Record<string, unknown>) : {};
    const currentSeq = typeof data[year] === 'number' ? data[year] : 0;
    const nextSeq = currentSeq + 1;

    // PAD_LENGTH is 4, so the format breaks after TF-YYYY-9999. Fail loud so
    // we don't silently roll into 5-digit numbers that break sorting/display.
    if (nextSeq > 9999) {
      throw new Error(
        `Account number counter exhausted for ${year} (reached TF-${year}-9999). ` +
          `Bump PAD_LENGTH and update any downstream parsers before continuing.`,
      );
    }

    tx.set(
      ref,
      {
        [year]: nextSeq,
        lastYear: year,
        lastSeq: nextSeq,
        updatedAt: new Date().toISOString(),
      },
      { merge: true },
    );

    return formatAccountNumber(year, nextSeq);
  });
}

/**
 * Backfill existing customers that don't have an account number yet.
 * Processes in chronological order of createdAt so oldest customers get the lowest numbers.
 * Only touches records belonging to the given tenant (or legacy records with no tenantId,
 * which are treated as belonging to the current tenant during the migration window).
 * Returns the count of customers updated.
 */
export async function backfillAccountNumbers(tenantId: string): Promise<number> {
  const q = query(
    collection(db, 'customers'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    orderBy('createdAt', 'asc'),
  );
  const snapshot = await getDocs(q);

  const missing = snapshot.docs.filter((d) => {
    const data = d.data() as Customer;
    return !data.accountNumber;
  });

  if (missing.length === 0) return 0;

  let updated = 0;
  const BATCH_SIZE = 400;

  for (let i = 0; i < missing.length; i += BATCH_SIZE) {
    const chunk = missing.slice(i, i + BATCH_SIZE);
    const batch = writeBatch(db);

    for (const docSnap of chunk) {
      const accountNumber = await reserveAccountNumber(tenantId);
      batch.update(docSnap.ref, {
        accountNumber,
        updatedAt: new Date().toISOString(),
      });
      updated++;
    }

    await batch.commit();
  }

  return updated;
}

/**
 * Preview the next account number without reserving it.
 * Useful for showing "Next customer will be TF-2026-0042" in admin UI.
 */
export async function peekNextAccountNumber(tenantId: string): Promise<string> {
  const year = new Date().getFullYear();
  const snap = await getDoc(counterRef(tenantId));
  const data = snap.exists() ? (snap.data() as Record<string, unknown>) : {};
  const currentSeq = typeof data[year] === 'number' ? data[year] : 0;
  return formatAccountNumber(year, currentSeq + 1);
}
